import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Tooltip, Typography, useElementIsEllipsis } from '@kc-console/common-ui';
import { useDataTableSafeContext } from '@kc-console/shared-ui';

interface RelatedServicesCellProps {
  services: string[];
}

interface TextEllipsisProps {
  textEllipsis: boolean;
}

/**
 * 이벤트 테이블의 "대상 서비스" 셀.
 * @description
 * - 첫 번째 서비스명을 그대로 노출하고, 추가 서비스가 있으면 `+N` 인디케이터를 붙인다.
 * - 값이 없으면 `-` 를 표시한다.
 * @example
 * <RelatedServicesCell services={['File Storage', 'MemStore']} />
 */
const RelatedServicesCell = ({ services }: RelatedServicesCellProps) => {
  const { ref: serviceNameRef, isEllipsis } = useElementIsEllipsis<HTMLSpanElement>();
  const { textEllipsis = true } = useDataTableSafeContext();

  if (!services || services.length === 0) {
    return <Typography variant="body/normal/md">-</Typography>;
  }

  const [primary, ...rest] = services;

  return (
    <Root textEllipsis={textEllipsis}>
      <Tooltip message={textEllipsis && isEllipsis ? primary : undefined}>
        <ServiceName
          ref={serviceNameRef}
          component="span"
          variant="body/normal/md"
          textEllipsis={textEllipsis}
        >
          {primary}
        </ServiceName>
      </Tooltip>
      {rest.length > 0 && <ExtraIndicator title={rest.join(', ')}>+{rest.length}</ExtraIndicator>}
    </Root>
  );
};

export default RelatedServicesCell;

const Root = styled('span', {
  shouldForwardProp: prop => prop !== 'textEllipsis',
})<TextEllipsisProps>(
  ({ textEllipsis }) => css`
    display: ${textEllipsis ? 'flex' : 'inline'};
    align-items: center;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    ${textEllipsis &&
    css`
      overflow: hidden;
    `}
  `,
);

const ServiceName = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textEllipsis',
})<TextEllipsisProps>(
  ({ textEllipsis }) => css`
    display: ${textEllipsis ? 'block' : 'inline'};
    min-width: 0;
    ${textEllipsis
      ? css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `
      : css`
          white-space: wrap;
        `}
  `,
);

const ExtraIndicator = styled('span')(
  ({ theme }) => css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 10px;
    background-color: ${theme.color['surface/neutral/weaker/default']};
    color: ${theme.color['text/weak/default']};
    font-size: 12px;
    line-height: 1;
  `,
);
